import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchSocios } from "../actions/socioActions";
import { fetchAllCuotas } from "../actions/cuotaActions";
import { Table, Modal, Button, Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import CustomPrintModal from "./CustomPrintModal";
import "./SocioList.css";

const SocioReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { socios, loading: loadingSocios, error: errorSocios } = useSelector(
    (state) => state.socio
  );
  const { cuotas, loading: loadingCuotas, error: errorCuotas } = useSelector(
    (state) => state.cuota
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("codreg");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterDate, setFilterDate] = useState(""); // Nueva variable para el filtro de fecha
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [content, setContent] = useState([]);
  const [printTitle, setPrintTitle] = useState("LISTA GENERAL DE SOCIOS");
  const [showConfirmExportModal, setShowConfirmExportModal] = useState(false);

  useEffect(() => {
    dispatch(fetchSocios());
    dispatch(fetchAllCuotas());
  }, [dispatch]);

  const sociosWithUnpaidQuotas = socios.map((socio) => {
    const socioCuotas = cuotas.filter((cuota) => cuota.idcodreg === socio.id);
    const unpaidQuotas = socioCuotas.filter((cuota) => !cuota.fepago).length;
    const lastPaidDate = socioCuotas
      .filter((cuota) => cuota.fepago)
      .map((cuota) => new Date(cuota.fepago))
      .sort((a, b) => b - a)[0]; // Obtener la última fecha de pago
    return { ...socio, unpaidQuotas, lastPaidDate };
  });

  const filteredSocios = (sociosWithUnpaidQuotas || [])
    .filter((socio) => socio && socio.codreg && socio.apPat)
    .filter(
      (socio) =>
        (filterStatus === "todos" ||
          (socio.estado != null && socio.estado.toString() === filterStatus)) &&
        (socio.codreg.includes(searchTerm) ||
          socio.apPat.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .filter((socio) => {
      // Filtro por fecha
      if (!filterDate) return true;
      if (socio.unpaidQuotas > 0) return false;
      return socio.lastPaidDate <= new Date(filterDate);
    })
    .sort((a, b) => {
      // Ordenamiento dinámico
      if (a[sortKey] < b[sortKey]) return sortOrder === "asc" ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const indexOfLastSocio = currentPage * itemsPerPage;
  const indexOfFirstSocio = indexOfLastSocio - itemsPerPage;
  const currentSocios = filteredSocios.slice(
    indexOfFirstSocio,
    indexOfLastSocio
  );

  const totalPages = Math.ceil(filteredSocios.length / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortKey(event.target.value);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handleFilterStatusChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredSocios.map((socio) => ({
        CodReg: socio.codreg,
        "Apellido Paterno": socio.apPat,
        "Apellido Materno": socio.apMat,
        Nombres: socio.nombres,
        CI: socio.ci,
        Estado: socio.estado === 1 ? "ACTIVO" : "INACTIVO",
        "Cuotas No Pagadas": socio.unpaidQuotas,
        "Última Fecha de Pago": socio.lastPaidDate
          ? socio.lastPaidDate.toISOString().split("T")[0]
          : "Sin datos",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Socios");

    XLSX.writeFile(workbook, "Listado_Socios.xlsx");
  };

  const handlePrintModalShow = () => {
    const sortedFilteredSocios = [...filteredSocios];
    const content = {
      head: [
        [
          "NroReg",
          "Apellido Paterno",
          "Apellido Materno",
          "Nombres",
          "CI",
          "Cuotas No Pagadas",
          "Última Fecha de Pago",
        ],
      ],
      body: sortedFilteredSocios.map((socio) => [
        socio.codreg,
        socio.apPat,
        socio.apMat,
        socio.nombres,
        socio.ci,
        socio.unpaidQuotas,
        socio.lastPaidDate
          ? socio.lastPaidDate.toISOString().split("T")[0]
          : "Sin datos",
      ]),
    };

    let titleSuffix;
    switch (filterStatus) {
      case "1":
        titleSuffix = " - ACTIVOS";
        break;
      case "0":
        titleSuffix = " - INACTIVOS";
        break;
      default:
        titleSuffix = " - TODOS";
    }

    setPrintTitle(`LISTA GENERAL DE SOCIOS${titleSuffix}`);
    setContent(content);
    setShowPrintModal(true);
  };

  const closeModalPrint = () => {
    setShowPrintModal(false);
  };

  const handleShowConfirmExportModal = () => {
    setShowConfirmExportModal(true);
  };

  const handleConfirmExport = () => {
    handleExportToExcel();
    setShowConfirmExportModal(false);
  };

  const handleCancelExport = () => {
    setShowConfirmExportModal(false);
  };

  if (loadingSocios || loadingCuotas) return <div>Cargando datos...</div>;
  if (errorSocios || errorCuotas)
    return (
      <div className="error-message">
        Error al cargar datos: {errorSocios || errorCuotas}
      </div>
    );

  return (
    <>
      <div className="list1-container container-list">
        <h2>LISTA GENERAL DE SOCIOS</h2>
        <div className="row w-100 mb-2">
          <div className="col-auto mt-2">
            <label htmlFor="items-per-page">Registros por página:</label>
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="col-auto mt-2">
            <label htmlFor="status-filter">Mostrar:</label>
            <select
              id="status-filter"
              value={filterStatus}
              onChange={handleFilterStatusChange}
            >
              <option value="todos">TODOS</option>
              <option value="1">ACTIVOS</option>
              <option value="0">INACTIVOS</option>
            </select>
          </div>
          <div className="col-auto mt-2">
            <label htmlFor="filter-date">Fecha Máxima Última Cuota Pagada:</label>
            <Form.Control
              id="filter-date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
          <div className="col-auto mt-2">
            <label htmlFor="sort">Ordenar por:</label>
            <select id="sort" value={sortKey} onChange={handleSortChange}>
              <option value="codreg">NroReg</option>
              <option value="apPat">Apellido Paterno</option>
              <option value="unpaidQuotas">Cuotas No Pagadas</option>
            </select>
            <select
              id="order"
              value={sortOrder}
              onChange={handleSortOrderChange}
            >
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          </div>
        </div>
        <div className="table-container">
          <Table hover striped size="sm" bordered>
            <thead>
              <tr>
                <th>NroReg</th>
                <th>Apellido Paterno</th>
                <th>Apellido Materno</th>
                <th>Nombres</th>
                <th>CI</th>
                <th>Cuotas No Pagadas</th>
                <th>Última Fecha de Pago</th>
              </tr>
            </thead>
            <tbody>
              {currentSocios.map((socio) => (
                <tr key={socio.id}>
                  <td>{socio.codreg}</td>
                  <td>{socio.apPat}</td>
                  <td>{socio.apMat}</td>
                  <td>{socio.nombres}</td>
                  <td>{socio.ci}</td>
                  <td>{socio.unpaidQuotas}</td>
                  <td>
                    {socio.lastPaidDate
                      ? socio.lastPaidDate.toISOString().split("T")[0]
                      : "Sin datos"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="text-center mb-3 button-group">
          <button
            onClick={() => navigate("/")}
            className="button btn-cancel"
          >
            Volver
          </button>
          <button
            onClick={handlePrintModalShow}
            className="button btn-accent"
          >
            Imprimir Lista
          </button>
          <button
            onClick={handleShowConfirmExportModal}
            className="button btn-accent"
          >
            Exportar a Excel
          </button>
        </div>
        <div className="pagination-controls">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="btn btn-outline-primary"
          >
            Anterior
          </button>
          <span className="mx-2">
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="btn btn-outline-primary"
          >
            Siguiente
          </button>
        </div>
        <CustomPrintModal
          show={showPrintModal}
          handleClose={closeModalPrint}
          title={printTitle}
          content={content}
        />
        <Modal show={showConfirmExportModal} onHide={handleCancelExport}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Exportación</Modal.Title>
          </Modal.Header>
          <Modal.Body>¿Estás seguro de que deseas exportar el listado a Excel?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancelExport}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmExport}>
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default SocioReport;
